import { useEffect, useState } from 'react';
import type { GetRecipeResponse, Suggestion, SuggestionPayload } from '@mlc/shared';
import { createSuggestion, listSuggestions, resolveSuggestion } from './api';
import { Banner, Button, Select, TextInput } from './ui';

/**
 * Propositions de modification (US-67).
 *
 * Sur la recette d'un autre, deux gestes coexistent : **copier** — divergence
 * assumée, la bibliothèque se remplit de variantes — et **proposer**, qui est
 * le geste de convergence. Le second manquait.
 *
 * ### Ce que cet écran montre
 *
 * L'**avant** et l'**après**, côte à côte. C'est tout l'intérêt d'un changement
 * décrit en données plutôt qu'en prose : « la pâte gagne à reposer plus
 * longtemps » ne s'applique pas en un geste ; « `rest_min` passe de 30 à 90 »,
 * si.
 *
 * ### Ce qu'il ne montre pas
 *
 * Aucun historique de versions. Une proposition acceptée **devient l'état
 * courant** ; l'historique est le fil des propositions, avec leur sort. C'est
 * la frontière avec V3, et elle est écrite au §1 de `CLAUDE.md`.
 */

/** Les cinq natures proposables, avec ce qu'il faut saisir pour chacune. */
const NATURES = [
  { kind: 'titre', label: 'Le titre' },
  { kind: 'description', label: 'La description' },
  { kind: 'temps', label: 'Un temps (repos, préparation, cuisson)' },
  { kind: 'ingredient', label: 'Un ingrédient' },
  { kind: 'step', label: 'Une étape' },
] as const;

/** Une valeur de proposition, en français. `null` et l'absence se disent, plutôt que de laisser un blanc. */
function decrire(payload: SuggestionPayload): string {
  switch (payload.kind) {
    case 'titre':
      return payload.title === '' ? '(vide)' : payload.title;
    case 'description':
      return payload.description ?? '(aucune)';
    case 'temps': {
      const morceaux = [
        payload.prep_min === undefined ? null : `préparation ${payload.prep_min ?? '—'} min`,
        payload.rest_min === undefined ? null : `repos ${payload.rest_min ?? '—'} min`,
        payload.cook_min === undefined ? null : `cuisson ${payload.cook_min ?? '—'} min`,
      ].filter((morceau): morceau is string => morceau !== null);
      return morceaux.length === 0 ? '(inchangé)' : morceaux.join(' · ');
    }
    case 'ingredient':
      return payload.name === ''
        ? '(nouvel ingrédient)'
        : `${payload.quantity === null ? '' : String(payload.quantity)} ${payload.unit ?? ''} ${payload.name}`.trim();
    case 'step':
      return payload.instruction === '' ? '(nouvelle étape)' : payload.instruction;
  }
}

export interface SuggestionPanelProps {
  detail: GetRecipeResponse;
  /** `true` quand le demandeur peut modifier la recette : il ne propose pas, il reçoit. */
  proprietaire: boolean;
  /** Une proposition acceptée a changé la recette : la fiche doit se relire. */
  onApplied: () => void;
}

export function SuggestionPanel({ detail, proprietaire, onApplied }: SuggestionPanelProps): React.JSX.Element | null {
  const [propositions, setPropositions] = useState<Suggestion[]>([]);
  const [erreur, setErreur] = useState<string | null>(null);
  const [ouvert, setOuvert] = useState(false);
  const [nature, setNature] = useState<SuggestionPayload['kind']>('temps');
  const [valeur, setValeur] = useState('');
  const [message, setMessage] = useState('');

  useEffect(() => {
    const controller = new AbortController();
    Promise.resolve()
      .then(() => listSuggestions(detail.id, controller.signal))
      .then((liste) => { setPropositions(Array.isArray(liste) ? liste : []); })
      .catch(() => { /* un fil vide vaut mieux qu'une fiche cassée */ });
    return () => { controller.abort(); };
  }, [detail.id]);

  function appliquer(promesse: Promise<Suggestion[]>, relire: boolean): void {
    setErreur(null);
    promesse
      .then((liste) => {
        setPropositions(Array.isArray(liste) ? liste : []);
        setOuvert(false);
        setValeur('');
        setMessage('');
        if (relire) onApplied();
      })
      .catch((e: unknown) => { setErreur(e instanceof Error ? e.message : String(e)); });
  }

  /** Le changement, construit depuis la saisie. Une valeur vide ne propose rien. */
  function payloadDepuisLaSaisie(): SuggestionPayload | null {
    const propre = valeur.trim();
    if (propre === '') return null;
    switch (nature) {
      case 'titre':
        return { kind: 'titre', title: propre };
      case 'description':
        return { kind: 'description', description: propre };
      case 'temps': {
        const minutes = Number(propre);
        if (!Number.isFinite(minutes) || minutes < 0) return null;
        return { kind: 'temps', rest_min: minutes };
      }
      case 'ingredient':
        return { kind: 'ingredient', ingredient_id: null, name: propre, quantity: null, unit: null, note: null };
      case 'step':
        return { kind: 'step', step_id: null, instruction: propre };
    }
  }

  const ouvertes = propositions.filter((proposition) => proposition.status === 'ouverte');
  const resolues = propositions.filter((proposition) => proposition.status !== 'ouverte');
  // Rien à montrer et rien à proposer : le panneau n'existe pas.
  if (propositions.length === 0 && proprietaire) return null;

  return (
    <section aria-labelledby="titre-propositions" className="flex flex-col gap-3">
      <h2 id="titre-propositions" className="text-heading-2">
        Propositions {ouvertes.length > 0 && <span className="text-data text-base font-normal">({ouvertes.length})</span>}
      </h2>

      {erreur !== null && <Banner tone="danger">{erreur}</Banner>}

      {propositions.length === 0 && (
        <p className="text-sm text-foreground-secondary">
          Vous pouvez proposer un changement à {detail.owner_name ?? 'la maison'} : la recette reste la sienne,
          c’est elle ou lui qui décide.
        </p>
      )}

      <ul className="flex flex-col gap-3">
        {[...ouvertes, ...resolues].map((proposition) => (
          <li key={proposition.id} className="flex flex-col gap-2 rounded-card border border-border p-3">
            <p className="text-sm">
              <span className="font-medium">{proposition.member_name ?? 'La maison'}</span>
              {proposition.status !== 'ouverte' && (
                <span className="text-foreground-muted">
                  {' · '}
                  {proposition.status === 'acceptee' ? 'acceptée' : proposition.status === 'refusee' ? 'refusée' : 'retirée'}
                </span>
              )}
            </p>
            {proposition.message !== null && <p className="text-sm text-foreground-secondary">{proposition.message}</p>}
            {/* Avant / après, côte à côte : c'est ce qui permet de décider sans
                aller relire la fiche. */}
            <dl className="grid grid-cols-1 gap-1 text-sm sm:grid-cols-2">
              <div>
                <dt className="text-meta">Aujourd’hui</dt>
                <dd className="line-through opacity-70">{decrire(proposition.before)}</dd>
              </div>
              <div>
                <dt className="text-meta">Proposé</dt>
                <dd className="font-medium">{decrire(proposition.payload)}</dd>
              </div>
            </dl>
            {(proposition.can_resolve || proposition.can_withdraw) && (
              <div className="flex flex-wrap gap-2">
                {proposition.can_resolve && (
                  <>
                    {/* En bloc, jamais ligne par ligne : une interface de
                        fusion pour trois personnes serait une machinerie sans
                        usage. */}
                    <Button
                      type="button"
                      variant="primary"
                      size="compact"
                      onClick={() => { appliquer(resolveSuggestion(detail.id, proposition.id, 'acceptee'), true); }}
                    >
                      Accepter
                    </Button>
                    <Button
                      type="button"
                      variant="secondary"
                      size="compact"
                      onClick={() => { appliquer(resolveSuggestion(detail.id, proposition.id, 'refusee'), false); }}
                    >
                      Refuser
                    </Button>
                  </>
                )}
                {proposition.can_withdraw && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="compact"
                    onClick={() => { appliquer(resolveSuggestion(detail.id, proposition.id, 'retiree'), false); }}
                  >
                    Retirer ma proposition
                  </Button>
                )}
              </div>
            )}
          </li>
        ))}
      </ul>

      {/* On ne se propose rien à soi-même : sur sa propre recette, on modifie. */}
      {!proprietaire && (
        ouvert ? (
          <div className="flex flex-col gap-2 rounded-card border border-border p-3">
            <label className="flex flex-col gap-1 text-sm">
              Ce que je propose de changer
              <Select
                aria-label="Nature de la proposition"
                value={nature}
                onChange={(e) => { setNature(e.target.value as SuggestionPayload['kind']); setValeur(''); }}
              >
                {NATURES.map(({ kind, label }) => <option key={kind} value={kind}>{label}</option>)}
              </Select>
            </label>
            <label className="flex flex-col gap-1 text-sm">
              {nature === 'temps' ? 'Nouveau temps de repos, en minutes' : 'La nouvelle valeur'}
              <TextInput value={valeur} onChange={(e) => { setValeur(e.target.value); }} />
            </label>
            <label className="flex flex-col gap-1 text-sm">
              Pourquoi (facultatif)
              <TextInput value={message} onChange={(e) => { setMessage(e.target.value); }} />
            </label>
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                variant="primary"
                size="compact"
                disabled={payloadDepuisLaSaisie() === null}
                onClick={() => {
                  const payload = payloadDepuisLaSaisie();
                  if (payload === null) return;
                  appliquer(
                    createSuggestion(detail.id, { payload, message: message.trim() === '' ? null : message }),
                    false,
                  );
                }}
              >
                Envoyer la proposition
              </Button>
              <Button type="button" variant="ghost" size="compact" onClick={() => { setOuvert(false); }}>
                Annuler
              </Button>
            </div>
          </div>
        ) : (
          <Button type="button" variant="secondary" className="w-fit" onClick={() => { setOuvert(true); }}>
            Proposer un changement
          </Button>
        )
      )}
    </section>
  );
}
